import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds } from "../../helpers/karkhanaEnabled";
import { discardPaySheet, finalisePaySheet, getPaySheet, paySheetPerson, refreshPaySheet } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import PaySheetPrint from "./pay/PaySheetPrint";
import CorrectionLines from "./pay/CorrectionLines";
import { fmtDay, rupees } from "./rates/rateModel";
import { newRequestId } from "./rounds/entryQueue";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import "./pay/karkhana-pay.scss";
import "./pay/karkhana-pay-sheet.scss";
import { useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import HandOverDialog from "./pay/HandOverDialog";
import { kp } from "./karkhanaPaths";
import { useKarkhanaTitle } from "./ui/addressState";
import { isDiscardedSheet, isDraftSheet } from "./pay/paySheetModel";
import { useCashbookStatus } from "../../helpers/cashbookStatus";
import { useCashPrint } from "./cashbook/print/cashPrintFlow";
import { listPaySheetPrints } from "../../services/cashBookApi";
import type { PrintView } from "./cashbook/types";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

/**
 * One pay sheet: print it (or save it as a PDF) for signatures, and mark each person paid as the
 * cash is handed over. A row whose account changed since printing asks for a refresh and reprint.
 */
const KarkhanaPaySheetPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const role = readRole();
  const [sheet, setSheet] = useState<any>(null);
  const [busy, setBusy] = useState("");
  // Cash book add-on: Print freezes a numbered print on the server (V1_SPEC 11.4 E3). Off: unchanged.
  const cashbook = useCashbookStatus();
  const cashOn = cashbook.loaded && cashbook.enabled;
  const cashPrint = useCashPrint();
  const [livePrint, setLivePrint] = useState<PrintView | null>(null);
  useEffect(() => {
    if (!cashOn) return;
    let live = true;
    listPaySheetPrints(id)
      .then((res) => live && setLivePrint(res?.success ? (res.data?.prints || []).find((p) => p.state === "LIVE") || null : null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [cashOn, id]);

  const load = useCallback(async () => {
    try {
      const res: any = await getPaySheet(id);
      if (res?.success) setSheet(res.data);
      else notifications({ title: t("Error"), message: t(res?.message || "Could not load the sheet"), type: "danger" });
    } catch {
      notifications({ title: t("No connection"), message: t("The pay sheet needs a connection."), type: "warning" });
    }
  }, [id]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  // Opened from "Print / PDF" on the pay sheet page: print as soon as the sheet is on screen.
  const printOnOpen = new URLSearchParams(window.location.search).get("print") === "1";
  const cashSheet = cashOn && !!sheet && !isDraftSheet(sheet) && !isDiscardedSheet(sheet);
  useEffect(() => {
    if (!printOnOpen || !sheet || !cashbook.loaded) return;
    if (cashSheet) {
      // With the Cash book add-on the paper people sign is the frozen, numbered print.
      history.replace({ search: "" });
      cashPrint.start(id);
      return;
    }
    // Clear ?print=1 only after printing: clearing it first re-renders and cancels the timer.
    const timer = window.setTimeout(() => {
      window.print();
      history.replace({ search: "" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [printOnOpen, sheet, history, cashbook.loaded, cashSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setBusy("refresh");
    try {
      const res: any = await refreshPaySheet(id);
      if (res?.success) {
        setSheet(res.data);
        notifications({ title: t("Refreshed"), message: t("Print it again before paying from paper"), type: "info" });
      } else notifications({ title: t("Error"), message: t(res?.message || "Could not refresh"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  // A draft (saved by the AI assistant): the owner checks it, then makes it final or throws it away.
  const decideDraft = async (finalise: boolean) => {
    if (!finalise && !window.confirm(t("Discard this draft pay sheet?"))) return;
    setBusy(finalise ? "finalise" : "discard");
    try {
      const res: any = await (finalise ? finalisePaySheet(id) : discardPaySheet(id));
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not save"), type: "danger" });
        load();
        return;
      }
      if (finalise) {
        setSheet(res.data);
        notifications({ title: t("Finalised"), message: t("Print it again before paying from paper"), type: "success" });
      } else {
        notifications({ title: t("Draft discarded"), message: "", type: "info" });
        history.replace(kp.paySheets());
      }
    } catch {
      notifications({ title: t("No connection"), message: t("This needs a connection. Nothing was saved."), type: "warning" });
    } finally {
      setBusy("");
    }
  };

  const [paying, setPaying] = useState<any>(null);

  const pay = async (l: any, body: { paid: string; cut: string; method: string; recoveryPerPay?: string }) => {
    setBusy(l.personId);
    try {
      const res: any = await paySheetPerson(id, l.personId, { ...body, clientRequestId: newRequestId() });
      if (!res?.success) {
        notifications({ title: t("Not paid"), message: t(res?.message || "Could not record"), type: "danger" });
        // Someone else may have just paid them: show the sheet as it is now.
        setPaying(null);
        load();
        return;
      }
      setSheet(res.data);
      setPaying(null);
      const now = (res.data?.lines || []).find((x: any) => x.personId === l.personId);
      if (now?.paidByName && now.paidAmount !== undefined && String(now.paidAmount) !== String(body.paid)) {
        // Already paid from this sheet before this tap reached the server.
        notifications({ title: t("Already paid"), message: t("{{code}} was already paid {{amount}} by {{who}}", { code: l.code, amount: rupees(now.paidAmount), who: now.paidByName }), type: "info" });
        return;
      }
      notifications({ title: t("Paid"), message: `${l.code}: ${rupees(body.paid)}`, type: "success" });
    } catch {
      notifications({ title: t("No connection"), message: t("Paying needs a connection. Nothing was recorded."), type: "warning" });
    } finally {
      setBusy("");
    }
  };

  useKarkhanaTitle(sheet ? `Pay sheet ${fmtDay(sheet.from)} – ${fmtDay(sheet.to)}` : null);
  if (!canUseRounds(role)) return null;
  if (!sheet) return <div className="gi-registry-page kw-page kr-rounds"><div className="kw-empty">{t("Loading…")}</div></div>;
  const lines: any[] = sheet.lines || [];
  const waiting = new Set<string>(sheet.waitingPersonIds || []);
  const unpaid = lines.filter((l) => !l.paid);
  const draft = isDraftSheet(sheet);
  const discarded = isDiscardedSheet(sheet);
  const owner = role === "ORGANIZATION_ADMIN";

  return (
    <div className="gi-registry-page kw-page kr-rounds kps-screen">
      <PageHeader
        title={
          <>
            {fmtDay(sheet.from)} – {fmtDay(sheet.to)}
            {draft ? <span className="kw-chip is-review" style={{ marginLeft: 8 }}>{t("Draft")}</span> : null}
            {discarded ? <span className="kw-chip" style={{ marginLeft: 8 }}>{t("Discarded")}</span> : null}
          </>
        }
        back={kp.paySheets()}
        backLabel={t("All sheets")}
      />
      <div className="kpay-balances">
        <div className="kpay-balance is-due">
          <span>{t("Net to pay")}</span>
          <strong>{rupees(sheet.totals?.netDue)}</strong>
        </div>
        <div className="kpay-balance">
          <span>{t("Paid")}</span>
          <strong>
            {lines.length - unpaid.length} / {lines.length}
          </strong>
        </div>
      </div>
      <div className="kw-toolbar">
        {cashSheet ? (
          <Button className="ResetBtn" disabled={cashPrint.busy} onClick={() => cashPrint.start(id)}>
            {cashPrint.busy ? t("Printing…") : t("Print")}
          </Button>
        ) : (
          <Button className="ResetBtn" onClick={() => window.print()}>
            {t("Print / Save PDF")}
          </Button>
        )}
        {cashSheet && livePrint ? (
          livePrint.checked ? (
            <span className="kw-chip is-ok">{t("Checked ✓")} · {livePrint.sheetNo}</span>
          ) : livePrint.pagesSent.length >= livePrint.pages ? (
            <span className="kw-chip is-ok">{t("Sent ✓")} · {livePrint.sheetNo}</span>
          ) : (
            <Link to={kp.cashCamera({ kind: "PAY_SHEET", printId: livePrint.printId })} className="btn btn-outline-secondary">
              {t("Send photo")} · {livePrint.sheetNo}
            </Link>
          )
        ) : null}
        {discarded ? null : (
          <Button outline disabled={busy === "refresh"} onClick={refresh}>
            {busy === "refresh" ? t("Refreshing…") : t("Refresh")}
          </Button>
        )}
      </div>
      {discarded ? (
        <p className="kw-muted">{t("This draft was discarded. Nobody is paid from it.")}</p>
      ) : draft ? (
        <>
          <p className="kw-muted">
            {t(owner
              ? "A draft made by the AI assistant. Check it and print it if you like, then Finalise to pay people from it, or Discard it."
              : "A draft made by the AI assistant. Nobody is paid from it until the owner finalises it.")}
          </p>
          {owner ? (
            <div className="kw-toolbar">
              <Button className="ResetBtn" disabled={!!busy} onClick={() => decideDraft(true)}>
                {busy === "finalise" ? t("Saving…") : t("Finalise")}
              </Button>
              <Button outline color="danger" disabled={!!busy} onClick={() => decideDraft(false)}>
                {busy === "discard" ? t("Saving…") : t("Discard draft")}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <p className="kw-muted">
          {t("Print the sheet, pay each person and get their signature, then tap Pay on their row. If someone's work changed after printing, Refresh and print again.")}
        </p>
      )}

      {lines.map((l) => (
        <div key={l.personId} className={`kw-card kps-row${l.paid ? " is-paid" : ""}${waiting.has(l.personId) && !l.paid ? " is-waiting" : ""}`}>
          <div className="kr-pay__row">
            <strong>
              <span className="kw-code">{l.code}</span> {l.name}
            </strong>
            <strong>{rupees(l.paid ? l.paidAmount : l.netDue)}</strong>
          </div>
          <div className="kw-muted">
            Earned {rupees(l.periodEarned)}
            {Number(l.earlierDue) > 0 ? ` · ${t("earlier due {{amount}}", { amount: rupees(l.earlierDue) })}` : ""}
            {Number(l.earlierDue) < 0 ? ` · ${t("already paid {{amount}}", { amount: rupees(-Number(l.earlierDue)) })}` : ""}
            {Number(l.paid ? l.paidCut : l.cut) ? ` · ${t("advance cut {{amount}}", { amount: rupees(l.paid ? l.paidCut : l.cut) })}` : ""}
            {Number(l.advanceOwed) ? ` · ${t("advance owed {{amount}}", { amount: rupees(l.advanceOwed) })}` : ""}
          </div>
          <CorrectionLines line={l} />
          {l.issues ? <div className="kw-muted">{l.issues} item(s) not counted yet — see their Pay account</div> : null}
          <div className="kw-toolbar" style={{ marginTop: 8, marginBottom: 0 }}>
            {discarded && !l.paid ? (
              <span className="kw-chip">{t("Discarded")}</span>
            ) : draft && !l.paid ? (
              // The server refuses Pay on a draft; say why instead of offering it.
              <span className="kw-chip is-review">{t("Finalise to pay")}</span>
            ) : l.paid ? (
              <span className="kw-chip is-ok">
                {t("Paid {{amount}} by {{name}}", { amount: rupees(l.paidAmount), name: l.paidByName })}
                {Number(l.paidExtraAdvance) > 0 ? ` · ${t("{{amount}} as advance", { amount: rupees(l.paidExtraAdvance) })}` : ""}
              </span>
            ) : waiting.has(l.personId) ? (
              // A worker a supervisor added: Pay waits for the owner's confirmation (the server refuses it until then).
              <>
                <span className="kw-chip is-review">{t("Waiting for owner")}</span>
                {role === "ORGANIZATION_ADMIN" ? (
                  <Link to={kp.workers()} className="btn btn-link btn-sm">
                    {t("Confirm in Workers")}
                  </Link>
                ) : null}
              </>
            ) : Number(l.netDue) + Number(l.cut) <= 0 ? (
              <span className="kw-chip">{t("Nothing to pay")}</span>
            ) : (
              <Button size="sm" className="ResetBtn" disabled={!!busy} onClick={() => setPaying(l)}>
                {busy === l.personId ? t("Saving…") : t("Pay {{amount}}", { amount: rupees(l.netDue) })}
              </Button>
            )}
            <Link to={kp.pay({ person: l.personId })} className="btn btn-link btn-sm">
              {t("Account")}
            </Link>
          </div>
        </div>
      ))}

      {cashPrint.element}
      <HandOverDialog line={paying} busy={!!busy} onCancel={() => setPaying(null)} onPay={(body) => pay(paying, body)} />

      <h3 className="kr-group-title">{t("Sheet preview")}</h3>
      <div className="kps-preview">
        <PaySheetPrint sheet={sheet} />
      </div>
    </div>
  );
};

export default KarkhanaPaySheetPage;
